import React from 'react';
import Icon from '../../../components/AppIcon';

const OverviewTab = ({ patient }) => {
  const allergies = [
    { name: "Penicillin", severity: "High", reaction: "Anaphylaxis" },
    { name: "Shellfish", severity: "Medium", reaction: "Hives, swelling" },
    { name: "Latex", severity: "Low", reaction: "Skin irritation" }
  ];

  const conditions = [
    { name: "Hypertension", status: "Active", diagnosed: "2020-03-15", severity: "Moderate" },
    { name: "Type 2 Diabetes", status: "Active", diagnosed: "2019-08-22", severity: "Well-controlled" },
    { name: "Asthma", status: "Inactive", diagnosed: "2015-01-10", severity: "Mild" }
  ];

  const medications = [
    { name: "Lisinopril 10mg", dosage: "Once daily", prescribedBy: "Dr. Johnson", startDate: "2020-03-15", status: "Active" },
    { name: "Metformin 500mg", dosage: "Twice daily", prescribedBy: "Dr. Smith", startDate: "2019-08-22", status: "Active" },
    { name: "Albuterol Inhaler", dosage: "As needed", prescribedBy: "Dr. Brown", startDate: "2021-05-10", status: "Active" }
  ];

  const vitalSigns = [
    { label: "Blood Pressure", value: "128/82 mmHg", status: "normal", date: "2025-09-20" },
    { label: "Heart Rate", value: "72 bpm", status: "normal", date: "2025-09-20" },
    { label: "Temperature", value: "98.6°F", status: "normal", date: "2025-09-20" },
    { label: "Weight", value: "165 lbs", status: "normal", date: "2025-09-18" },
    { label: "Height", value: "5\'8\"", status: "normal", date: "2024-01-15" },
    { label: "BMI", value: "25.1", status: "normal", date: "2025-09-18" }
  ];

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'bg-error text-error-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'low': return 'bg-success text-success-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-success text-success-foreground';
      case 'inactive': return 'bg-muted text-muted-foreground';
      case 'well-controlled': return 'bg-accent text-accent-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getVitalStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-success';
      case 'high': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Vital Signs */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-clinical">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} />
          Latest Vital Signs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vitalSigns?.map((vital, index) => (
            <div key={index} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{vital?.label}</span>
                <span className="text-xs text-muted-foreground">{vital?.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">{vital?.value}</span>
                <Icon name="CheckCircle" size={16} className={getVitalStatusColor(vital?.status)} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Medical Conditions */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-clinical">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Heart" size={20} />
          Medical Conditions
        </h3>
        <div className="space-y-3">
          {conditions?.map((condition, index) => (
            <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-medium text-foreground">{condition?.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(condition?.status)}`}>
                    {condition?.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>Diagnosed: {new Date(condition.diagnosed)?.toLocaleDateString()}</span>
                  <span>Severity: {condition?.severity}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Current Medications */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-clinical">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Pill" size={20} />
          Current Medications
        </h3>
        <div className="space-y-3">
          {medications?.map((medication, index) => (
            <div key={index} className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-medium text-foreground">{medication?.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(medication?.status)}`}>
                    {medication?.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>Dosage: {medication?.dosage}</span>
                  <span>Prescribed by: {medication?.prescribedBy}</span>
                  <span>Started: {new Date(medication.startDate)?.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Allergies */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-clinical">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="AlertTriangle" size={20} />
          Known Allergies
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allergies?.map((allergy, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{allergy?.name}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(allergy?.severity)}`}>
                  {allergy?.severity}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{allergy?.reaction}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;